import { FormatListBulleted as FormatListBulletedIcon } from 'components/icons';
import ViewCozyIcon from 'assets/images/icons/ViewCozy.svg?react';
import ListCreateButton from 'components/buttons/ListCreateButton';
import PageHeader from 'components/PageHeader';
import { Tooltip } from 'components/ui/tooltip';
import { ViewSwitch, ViewSwitchItem } from 'components/ViewSwitch';
import { useViewParam } from 'hooks/useViewParam';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ExpensesGroupedTable from './ExpensesGroupedTable';
import ExpensesTableWithFilters from './ExpensesTable';

const EXPENSE_VIEW_MODES = ['LIST', 'GROUPED'] as const;
type ViewMode = (typeof EXPENSE_VIEW_MODES)[number];

const ExpensesPage = (): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isExpenseEntryDisabled = useExpenseEntryDisabled();
    const isGroupedViewEnabled = useIsFeatureEnabled(FeatureFlag.EXPENSES_GROUPED_VIEW);
    const [viewMode, setViewMode] = useViewParam('expenses', EXPENSE_VIEW_MODES, 'LIST');
    // Off the flag Grouped does not exist, so a remembered or linked one still opens on List.
    const effectiveViewMode: ViewMode = isGroupedViewEnabled ? viewMode : 'LIST';

    // The two views share /expenses but have disjoint sort-key vocabularies — `amount-{CUR}`
    // is meaningful only in Grouped (client-side sort), while LIST sorts by JPA paths on Expense.
    // Carrying the URL `sort` across a toggle would feed `amount-USD` into /v1/expenses and break
    // JPA path resolution, so we strip it on every viewMode change.
    const handleViewModeChange = useCallback(
        (next: ViewMode) => {
            if (next === viewMode) return;
            setViewMode(next, (params) => params.delete('sort'));
        },
        [viewMode, setViewMode]
    );

    const viewModeToggle = isGroupedViewEnabled ? (
        <ViewSwitch value={effectiveViewMode} onChange={handleViewModeChange} aria-label={t('expenses.view.label')}>
            <Tooltip content={<FormattedMessage id="expenses.view.list-tooltip" />}>
                <ViewSwitchItem value="LIST">
                    <FormatListBulletedIcon fontSize="small" />
                    <FormattedMessage id="expenses.view.list" />
                </ViewSwitchItem>
            </Tooltip>
            <Tooltip content={<FormattedMessage id="expenses.view.grouped-tooltip" />}>
                <ViewSwitchItem value="GROUPED">
                    <ViewCozyIcon className="size-5 shrink-0" />
                    <FormattedMessage id="expenses.view.grouped" />
                </ViewSwitchItem>
            </Tooltip>
        </ViewSwitch>
    ) : undefined;

    // The span carries the tooltip: a disabled button fires no pointer events.
    const addButton = (
        <span>
            <ListCreateButton
                permissions={['CREATE.EXPENSES']}
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => navigateWithReturn('/expense/new')}
                disabled={isExpenseEntryDisabled}
                labelId="expenses.add-new-expense"
                newLabelId="new-entity.expense"
            />
        </span>
    );

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader
                    hostsList
                    titleId="expenses.expenses"
                    showBreadcrumb
                    buttons={
                        <div>
                            {isExpenseEntryDisabled ? (
                                <Tooltip content={t('expenses.integration-entry-disabled')}>{addButton}</Tooltip>
                            ) : (
                                addButton
                            )}
                        </div>
                    }
                />
                {effectiveViewMode === 'LIST' ? (
                    <ExpensesTableWithFilters quickFilterTrailing={viewModeToggle} />
                ) : (
                    <ExpensesGroupedTable quickFilterTrailing={viewModeToggle} />
                )}
            </div>
        </PaddedLayout>
    );
};

export default ExpensesPage;
